import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select, MultiSelect } from '..';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { 
  FiBriefcase, 
  FiFileText, 
  FiHash, 
  FiSettings, 
  FiDollarSign, 
  FiClock, 
  FiAlertCircle 
} from 'react-icons/fi';
import '../CompanyForm/style.css';

export default function ReasonForm({ companyId, companies = [], initialData, onSuccess, onCancel }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    company_id: '',
    companyIds: [],
    name: '',
    payroll_code: '',
    abates: 'false',
    prevents_clocking: 'false',
    deducts_va: 'false',
    deducts_vt: 'false',
  });

  const companyOptions = useMemo(() => {
    return companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
  }, [companies]);

  useEffect(() => {
    if (initialData) {
      const initialCompanyId = initialData.company_id || initialData.company?.id;
      setFormData({
        company_id: initialCompanyId ? String(initialCompanyId) : '',
        companyIds: [],
        name: initialData.name || '',
        payroll_code: initialData.payroll_code || '',
        abates: String(initialData.abates),
        prevents_clocking: String(initialData.prevents_clocking),
        deducts_va: String(initialData.deducts_va),
        deducts_vt: String(initialData.deducts_vt),
      });
    } else {
       setFormData({
        company_id: '',
        companyIds: companyId ? [String(companyId)] : [],
        name: '',
        payroll_code: '',
        abates: 'false',
        prevents_clocking: 'false',
        deducts_va: 'false',
        deducts_vt: 'false',
      });
    }
  }, [initialData, companyId]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  const [displayCompanyIds, setDisplayCompanyIds] = useState([]);

  useEffect(() => {
    if (formData.companyIds.length > 0 && formData.companyIds.length === companyOptions.length) {
       setDisplayCompanyIds(['all', ...formData.companyIds]);
    } else {
       setDisplayCompanyIds(formData.companyIds);
    }
  }, [formData.companyIds, companyOptions]);

  function handleCompanyChange(values) {
    if (values.includes('all')) {
      // If 'all' was NOT in previous display state, it means user just selected it -> Select All
      // If 'all' WAS in previous display state, it means user is deselecting it (but MultiSelect usually handles deselect by removing value)
      // Wait, MultiSelect returns the NEW array of values.
      
      const wasAllSelected = displayCompanyIds.includes('all');
      
      if (!wasAllSelected) {
         // User selected 'all'
         setFormData(prev => ({ ...prev, companyIds: companyOptions.map(o => o.value) }));
      } else {
         // User deselected 'all' or some other logic?
         // If 'all' is in values, it means it is still selected. 
         // But if user clicks 'all' again to deselect, 'all' will NOT be in values.
         
         // Actually, let's rely on standard logic:
         // If values includes 'all' and it wasn't fully selected before, select all.
         // If it was fully selected, and 'all' is still there, maybe user deselected something else?
         
         // Let's simplify:
         // If 'all' is present in the NEW values, we should check if we need to select all.
         // But if we are already selecting all, 'all' is just a visual artifact.
         
         // If user explicitly clicks 'all', it toggles.
         // However, MultiSelect onChange just gives us the new list.
         
         // Scenario 1: Nothing selected. User clicks "All". values=['all']. -> Select All.
         // Scenario 2: All selected (['all', '1', '2']). User clicks "All" to deselect. values=['1', '2']. -> Deselect All.
         // Scenario 3: Some selected (['1']). User clicks "All". values=['1', 'all']. -> Select All.
         
         // To detect Scenario 2 properly, we need to know if 'all' was removed. 
         // But here 'values' includes 'all' in Scenario 1 & 3. 
         
         // If 'all' is in values:
         // Check if we already had all companies selected.
         if (formData.companyIds.length === companyOptions.length) {
             // We had all selected. If 'all' is still here, it implies no change to 'all' itself?
             // But wait, if user deselects 'all', 'all' won't be in values.
             // So if 'all' IS in values, user might have clicked it (to select) or it was already there.
             
             // If we already have all selected, and 'all' is in values, and user deselected a specific company '1':
             // values will be ['all', '2', '3']. '1' is missing.
             // In this case, we should remove 'all' from display, and set companyIds to ['2', '3'].
             const newIds = values.filter(v => v !== 'all');
             setFormData(prev => ({ ...prev, companyIds: newIds }));
         } else {
             // Not all were selected. 'all' is present. User clicked 'all' to Select All.
             setFormData(prev => ({ ...prev, companyIds: companyOptions.map(o => o.value) }));
         }
      }
    } else {
      // 'all' is NOT in values.
      // Scenario A: User clicked 'all' to deselect it (was selected). -> Deselect All.
      // Scenario B: User clicked a specific company to deselect it. -> Remove that company.
      // Scenario C: User clicked a specific company to select it (was not selected). -> Add that company.
      
      // If we previously had all selected (meaning display included 'all'), and now 'all' is missing:
      if (displayCompanyIds.includes('all')) {
         // User explicitly deselected 'all'. -> Clear all.
         setFormData(prev => ({ ...prev, companyIds: [] }));
      } else {
         // standard change
         setFormData(prev => ({ ...prev, companyIds: values }));
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (isEdit) {
      if (!formData.company_id) {
          toast.error('Selecione uma empresa para editar o motivo.');
          return;
      }
    } else {
      if (formData.companyIds.length === 0) {
          toast.error('Selecione pelo menos uma empresa para cadastrar o motivo.');
          return;
      }
    }

    if (!formData.name.trim()) {
      toast.error('Nome do motivo é obrigatório');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        payroll_code: formData.payroll_code,
        abates: formData.abates === 'true',
        prevents_clocking: formData.prevents_clocking === 'true',
        deducts_va: formData.deducts_va === 'true',
        deducts_vt: formData.deducts_vt === 'true',
      };

      if (isEdit) {
        const targetCompanyId = formData.company_id;
        await Api.updateReason(targetCompanyId, initialData.id, payload);
        toast.success('Motivo atualizado com sucesso!');
      } else {
        const promises = formData.companyIds.map(cId => Api.createReason(cId, payload));
        await Promise.all(promises);
        toast.success(`Motivo criado para ${formData.companyIds.length} empresa(s)!`);
      }

      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Erro ao salvar motivo');
    } finally {
      setLoading(false);
    }
  }

  const booleanOptions = [
    { value: 'true', label: 'Sim' },
    { value: 'false', label: 'Não' },
  ];

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>Dados do Motivo</h3>
        <div className="form-grid">
          <div className="full-width">
            {isEdit ? (
               <Select
                  id="company_id"
                  label="Empresa *"
                  placeholder="SELECIONE UMA EMPRESA..."
                  value={formData.company_id}
                  onChange={handleChange}
                  icon={FiBriefcase}
                  options={companyOptions}
                  searchable
                  disabled={true}
                />
            ) : (
               <MultiSelect
                  id="companyIds"
                  label="Empresas *"
                  placeholder="SELECIONE AS EMPRESAS..."
                  value={displayCompanyIds}
                  onChange={handleCompanyChange}
                  icon={FiBriefcase}
                  options={[
                    { value: 'all', label: 'TODAS' },
                    ...companyOptions
                  ]}
                />
            )}
          </div>
          <div className="full-width">
            <Input
              id="name"
              label="Nome do Motivo *"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Consulta Médica"
              icon={FiFileText}
            />
          </div>
          <Input
            id="payroll_code"
            label="Código Folha"
            value={formData.payroll_code}
            onChange={handleChange}
            placeholder="Ex: 101"
            icon={FiHash}
          />
        </div>

        <h3 className="section-title">Configurações de Ponto</h3>
        <div className="form-grid">
          <Select
            id="abates"
            label="Abona Falta/Atraso?"
            options={booleanOptions}
            value={formData.abates}
            onChange={handleChange}
            icon={FiSettings}
          />
          <Select
            id="prevents_clocking"
            label="Impede Ponto?"
            options={booleanOptions}
            value={formData.prevents_clocking}
            onChange={handleChange}
            icon={FiClock}
          />
        </div>

        <h3 className="section-title">Configurações de Desconto</h3>
        <div className="form-grid">
          <Select
            id="deducts_va"
            label="Desconta VA?"
            options={booleanOptions}
            value={formData.deducts_va}
            onChange={handleChange}
            icon={FiDollarSign}
          />
          <Select
            id="deducts_vt"
            label="Desconta VT?"
            options={booleanOptions}
            value={formData.deducts_vt}
            onChange={handleChange}
            icon={FiDollarSign}
          />
        </div>
      </div>

      <div className="modal-actions">
        <Button type="button" className="cancel-button" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Motivo'}
        </Button>
      </div>
    </form>
  );
}
